import type {
  Case,
  CaseFilter,
  CaseNote,
  CaseNotePayload,
  CasePayload,
  CaseStep,
  CaseStepPayload,
  CaseStatus,
  ResolutionType,
} from "@/features/cases/types";
import { apiFetch } from "@/lib/api";

/**
 * The Django API wraps every body in a standard envelope
 * (`APIJSONRenderer`): `{ success, message, data }`. `apiFetch` returns that
 * verbatim, so this layer unwraps `data` and maps the snake_case read shape
 * onto the camelCase `Case`/`CaseStep`.
 */
interface Envelope<T> {
  success: boolean;
  message: string;
  data: T;
}

interface ApiCaseStep {
  id: string;
  description: string;
  position: number;
  due_date: string | null;
  is_completed: boolean;
  completed_at: string | null;
}

interface ApiCase {
  id: string;
  client: string;
  client_name: string;
  assigned_practitioner: string | null;
  assigned_practitioner_name: string;
  resolution_type: ResolutionType;
  resolution_type_display: string;
  status: CaseStatus;
  status_display: string;
  balance_cents: number;
  tax_years: string;
  opened_on: string;
  target_date: string | null;
  followup_date: string | null;
  followup_desc: string;
  next_due: string | null;
  days_until_due: number | null;
  closed_at: string | null;
  notes: string;
  steps: ApiCaseStep[];
  open_step_count: number;
  created_at: string;
  updated_at: string;
}

interface ApiCaseNote {
  id: string;
  text: string;
  is_pinned: boolean;
  is_system: boolean;
  author: number | null;
  author_name: string;
  created_at: string;
  updated_at: string;
}

function toStep(api: ApiCaseStep): CaseStep {
  return {
    id: api.id,
    description: api.description,
    position: api.position,
    dueDate: api.due_date,
    isCompleted: api.is_completed,
    completedAt: api.completed_at,
  };
}

function toCase(api: ApiCase): Case {
  return {
    id: api.id,
    client: api.client,
    clientName: api.client_name,
    assignedPractitioner: api.assigned_practitioner,
    assignedPractitionerName: api.assigned_practitioner_name,
    resolutionType: api.resolution_type,
    resolutionTypeDisplay: api.resolution_type_display,
    status: api.status,
    statusDisplay: api.status_display,
    balanceCents: api.balance_cents,
    taxYears: api.tax_years,
    openedOn: api.opened_on,
    targetDate: api.target_date,
    followupDate: api.followup_date,
    followupDesc: api.followup_desc,
    nextDue: api.next_due,
    daysUntilDue: api.days_until_due,
    closedAt: api.closed_at,
    notes: api.notes,
    steps: api.steps.map(toStep),
    openStepCount: api.open_step_count,
    createdAt: api.created_at,
    updatedAt: api.updated_at,
  };
}

function toNote(api: ApiCaseNote): CaseNote {
  return {
    id: api.id,
    text: api.text,
    isPinned: api.is_pinned,
    isSystem: api.is_system,
    author: api.author,
    authorName: api.author_name,
    createdAt: api.created_at,
    updatedAt: api.updated_at,
  };
}

/** Map a case-list filter tab to its query-string params. */
function filterParams(filter: CaseFilter): string {
  switch (filter) {
    case "open":
      return "&status=OPEN";
    case "completed":
      return "&status=COMPLETED";
    case "today":
      return "&due=today";
    case "week":
      return "&due=week";
    default:
      return "";
  }
}

// A client has at most a handful of cases, so one max-size page covers the lot.
const CASES_PAGE = "?page_size=100";

/** GET /cases/?client=[&status=|&due=] — the client's cases, firm-scoped. */
export async function listClientCases(
  clientId: string,
  filter: CaseFilter = "all",
): Promise<Case[]> {
  const body = await apiFetch<Envelope<ApiCase[]>>(
    `/cases/${CASES_PAGE}&client=${clientId}${filterParams(filter)}`,
  );
  return body.data.map(toCase);
}

/** How many firm cases the dashboard feed loads at once. Unlike a single
 * client's handful of cases, a whole firm can exceed this — so callers surface
 * a "refine with a filter" hint when the result hits the cap rather than
 * silently dropping the overflow. */
export const FIRM_FEED_LIMIT = 100;

export interface FirmCasesPage {
  cases: Case[];
  /** True when the result was capped at `FIRM_FEED_LIMIT` (more may exist). */
  truncated: boolean;
}

/** GET /cases/[?status=|?due=] — the whole firm's cases (dashboard feed). */
export async function listFirmCases(filter: CaseFilter = "open"): Promise<FirmCasesPage> {
  const body = await apiFetch<Envelope<ApiCase[]>>(
    `/cases/?page_size=${FIRM_FEED_LIMIT}${filterParams(filter)}`,
  );
  const cases = body.data.map(toCase);
  return { cases, truncated: cases.length >= FIRM_FEED_LIMIT };
}

/** GET /cases/:id/ */
export async function getCase(id: string): Promise<Case> {
  const body = await apiFetch<Envelope<ApiCase>>(`/cases/${id}/`);
  return toCase(body.data);
}

/** POST /cases/:id/complete/ — status → RESOLVED, stamps closed_at. */
export async function completeCase(id: string): Promise<Case> {
  const body = await apiFetch<Envelope<ApiCase>>(`/cases/${id}/complete/`, {
    method: "POST",
  });
  return toCase(body.data);
}

/** POST /cases/:id/reopen/ — status → ACTIVE, clears closed_at. */
export async function reopenCase(id: string): Promise<Case> {
  const body = await apiFetch<Envelope<ApiCase>>(`/cases/${id}/reopen/`, {
    method: "POST",
  });
  return toCase(body.data);
}

/** POST /cases/ */
export async function createCase(payload: CasePayload): Promise<Case> {
  const body = await apiFetch<Envelope<ApiCase>>("/cases/", {
    method: "POST",
    json: payload,
  });
  return toCase(body.data);
}

/** PATCH /cases/:id/ */
export async function updateCase(id: string, payload: CasePayload): Promise<Case> {
  const body = await apiFetch<Envelope<ApiCase>>(`/cases/${id}/`, {
    method: "PATCH",
    json: payload,
  });
  return toCase(body.data);
}

/** DELETE /cases/:id/ — soft-deletes server-side. */
export function deleteCase(id: string): Promise<void> {
  return apiFetch<void>(`/cases/${id}/`, { method: "DELETE" });
}

// --- Case steps (nested) -----------------------------------------------------

/** POST /cases/:id/steps/ */
export async function createCaseStep(
  caseId: string,
  payload: CaseStepPayload,
): Promise<CaseStep> {
  const body = await apiFetch<Envelope<ApiCaseStep>>(`/cases/${caseId}/steps/`, {
    method: "POST",
    json: payload,
  });
  return toStep(body.data);
}

/** PATCH /cases/:id/steps/:stepId/ */
export async function updateCaseStep(
  caseId: string,
  id: string,
  payload: CaseStepPayload,
): Promise<CaseStep> {
  const body = await apiFetch<Envelope<ApiCaseStep>>(`/cases/${caseId}/steps/${id}/`, {
    method: "PATCH",
    json: payload,
  });
  return toStep(body.data);
}

/** DELETE /cases/:id/steps/:stepId/ — hard delete. */
export function deleteCaseStep(caseId: string, id: string): Promise<void> {
  return apiFetch<void>(`/cases/${caseId}/steps/${id}/`, { method: "DELETE" });
}

/** POST /cases/:id/steps/reorder/ — `stepIds` in the new order. Returns the
 * reordered step list. */
export async function reorderCaseSteps(
  caseId: string,
  stepIds: string[],
): Promise<CaseStep[]> {
  const body = await apiFetch<Envelope<ApiCaseStep[]>>(
    `/cases/${caseId}/steps/reorder/`,
    { method: "POST", json: { step_ids: stepIds } },
  );
  return body.data.map(toStep);
}

// --- Case notes / activity ---------------------------------------------------

/** GET /cases/:id/notes/ — pinned first, then newest. */
export async function listCaseNotes(caseId: string): Promise<CaseNote[]> {
  const body = await apiFetch<Envelope<ApiCaseNote[]>>(
    `/cases/${caseId}/notes/${CASES_PAGE}`,
  );
  return body.data.map(toNote);
}

/** POST /cases/:id/notes/ */
export async function createCaseNote(
  caseId: string,
  payload: CaseNotePayload,
): Promise<CaseNote> {
  const body = await apiFetch<Envelope<ApiCaseNote>>(`/cases/${caseId}/notes/`, {
    method: "POST",
    json: payload,
  });
  return toNote(body.data);
}

/** PATCH /cases/:id/notes/:noteId/ */
export async function updateCaseNote(
  caseId: string,
  id: string,
  payload: CaseNotePayload,
): Promise<CaseNote> {
  const body = await apiFetch<Envelope<ApiCaseNote>>(`/cases/${caseId}/notes/${id}/`, {
    method: "PATCH",
    json: payload,
  });
  return toNote(body.data);
}

/** DELETE /cases/:id/notes/:noteId/ */
export function deleteCaseNote(caseId: string, id: string): Promise<void> {
  return apiFetch<void>(`/cases/${caseId}/notes/${id}/`, { method: "DELETE" });
}
